import React, { useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import TransferMap from './components/TransferMap';
import ControlPanel from './components/ControlPanel';
import StatsPanel from './components/StatsPanel';
import TransferTable from './components/TransferTable';
import { BarChart3, HelpCircle, X, ChevronDown, ChevronUp, Filter, Search, Calendar, Users, Building, Table } from 'lucide-react';
import './index.css';

function App() {
  const [selectedSellerLeague, setSelectedSellerLeague] = useState('All');
  const [selectedBuyerLeague, setSelectedBuyerLeague] = useState('All');
  const [minAmount, setMinAmount] = useState(1000000); // €1M+ default
  const [maxAmount, setMaxAmount] = useState(150000000);
  const [excludeOtherLeagues, setExcludeOtherLeagues] = useState(true); // Default to exclude other leagues
  const [showStats, setShowStats] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // Collapsed by default on desktop
  const [showGrouping, setShowGrouping] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Enhanced filter states
  const [selectedPosition, setSelectedPosition] = useState('All');
  const [selectedTransferType, setSelectedTransferType] = useState('Paid'); // Default to paid transfers only
  const [startDate, setStartDate] = useState('2025-06-01');
  const [endDate, setEndDate] = useState('2025-12-31');
  const [playerSearch, setPlayerSearch] = useState('');
  const [clubSearch, setClubSearch] = useState('');
  
  // Percentile filter states
  const [minPercentile, setMinPercentile] = useState(0);
  const [maxPercentile, setMaxPercentile] = useState(100);
  
  // Enhanced grouping states - only one can be active at a time
  const [groupingMode, setGroupingMode] = useState('none'); // 'none', 'league', 'position', 'date'
  
  // Focus state for translucent effect
  const [isFocused, setIsFocused] = useState(false);
  
  // Quick Views and Reset functionality
  const resetToDefaults = () => {
    setSelectedSellerLeague('All');
    setSelectedBuyerLeague('All');
    setMinAmount(1000000);
    setMaxAmount(150000000);
    setExcludeOtherLeagues(true);
    setSelectedPosition('All');
    setSelectedTransferType('Paid');
    setStartDate('2025-06-01');
    setEndDate('2025-12-31');
    setPlayerSearch('');
    setClubSearch('');
    setMinPercentile(0);
    setMaxPercentile(100);
    setGroupingMode('none');
  };
  
  const quickViews = {
    'Big Money Moves': () => {
      // Reset all filters first
      setSelectedSellerLeague('All');
      setSelectedBuyerLeague('All');
      setMinAmount(50000000); // €50M+
      setMaxAmount(150000000);
      setSelectedPosition('All');
      setSelectedTransferType('Paid');
      setStartDate('2025-06-01');
      setEndDate('2025-12-31');
      setPlayerSearch('');
      setClubSearch('');
      setMinPercentile(0);
      setMaxPercentile(100);
      setExcludeOtherLeagues(true);
      setGroupingMode('none'); // No grouping - show all transfers
    },
    'Premier League Focus': () => {
      // Reset all filters first
      setSelectedSellerLeague('Premier League');
      setSelectedBuyerLeague('Premier League');
      setMinAmount(1000000);
      setMaxAmount(150000000);
      setSelectedPosition('All');
      setSelectedTransferType('All');
      setStartDate('2025-06-01');
      setEndDate('2025-12-31');
      setPlayerSearch('');
      setClubSearch('');
      setMinPercentile(0);
      setMaxPercentile(100);
      setExcludeOtherLeagues(false);
      setGroupingMode('none'); // No grouping - show all transfers
    },
    'Top 5 Leagues': () => {
      // Reset all filters first
      setSelectedSellerLeague('All');
      setSelectedBuyerLeague('All');
      setMinAmount(1000000);
      setMaxAmount(150000000);
      setSelectedPosition('All');
      setSelectedTransferType('Paid');
      setStartDate('2025-06-01');
      setEndDate('2025-12-31');
      setPlayerSearch('');
      setClubSearch('');
      setMinPercentile(0);
      setMaxPercentile(100);
      setExcludeOtherLeagues(true);
      setGroupingMode('none'); // No grouping - show all transfers
    },
    'All Transfers': () => {
      // Reset all filters first
      setSelectedSellerLeague('All');
      setSelectedBuyerLeague('All');
      setMinAmount(0);
      setMaxAmount(150000000);
      setSelectedPosition('All');
      setSelectedTransferType('All');
      setStartDate('2025-06-01');
      setEndDate('2025-12-31');
      setPlayerSearch('');
      setClubSearch('');
      setMinPercentile(0);
      setMaxPercentile(100);
      setExcludeOtherLeagues(false);
      setGroupingMode('none'); // No grouping - show all transfers
    }
  };

  return (
    <div className="app">
      {/* Top Right Bar */}
      <div 
        className={`top-right-bar ${showMobileMenu ? 'mobile-menu-active' : ''} ${isFocused ? 'focused' : 'unfocused'}`}
        onMouseEnter={() => setIsFocused(true)}
        onMouseLeave={() => setIsFocused(false)}
      >
        <div className="app-title">
          <h1>Money In Motion</h1>
          <p>European Football Transfer Visualization</p>
        </div>
        
        {/* Mobile Toggle Button */}
        <button 
          className="mobile-toggle"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
        >
          {showMobileMenu ? 'Hide Controls' : 'Show Controls'}
        </button>
        
        {/* Search Section */}
        <div className="control-section">
          <button 
            className="section-toggle"
            onClick={() => setShowSearch(!showSearch)}
          >
            <Search size={16} />
            <span>Search</span>
            {showSearch ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {showSearch && (
            <div className="section-content">
              <div className="search-controls">
                <div className="search-row">
                  <label>Player Name:</label>
                  <input
                    type="text"
                    placeholder="Search players..."
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="search-row">
                  <label>Club Name:</label>
                  <input
                    type="text"
                    placeholder="Search clubs..."
                    value={clubSearch}
                    onChange={(e) => setClubSearch(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filters Section */}
        <div className="control-section">
          <button 
            className="section-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            <span>Filters</span>
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {showFilters && (
            <div className="section-content">
              <ControlPanel 
                selectedSellerLeague={selectedSellerLeague}
                setSelectedSellerLeague={setSelectedSellerLeague}
                selectedBuyerLeague={selectedBuyerLeague}
                setSelectedBuyerLeague={setSelectedBuyerLeague}
                minAmount={minAmount}
                setMinAmount={setMinAmount}
                maxAmount={maxAmount}
                setMaxAmount={setMaxAmount}
                selectedPosition={selectedPosition}
                setSelectedPosition={setSelectedPosition}
                selectedTransferType={selectedTransferType}
                setSelectedTransferType={setSelectedTransferType}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                excludeOtherLeagues={excludeOtherLeagues}
                setExcludeOtherLeagues={setExcludeOtherLeagues}
                minPercentile={minPercentile}
                setMinPercentile={setMinPercentile}
                maxPercentile={maxPercentile}
                setMaxPercentile={setMaxPercentile}
              />
            </div>
          )}
        </div>

        {/* Grouping Section */}
        <div className="control-section">
          <button 
            className="section-toggle"
            onClick={() => setShowGrouping(!showGrouping)}
          >
            <BarChart3 size={16} />
            <span>Grouping</span>
            {showGrouping ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {showGrouping && (
            <div className="section-content">
               <div className="grouping-controls">
                 <div className={`grouping-option ${groupingMode === 'none' ? 'active' : ''}`}>
                   <label onClick={() => setGroupingMode('none')}>
                     <div className="grouping-icon">⚫</div>
                     <span>No Grouping</span>
                     <input
                       type="radio"
                       name="grouping"
                       value="none"
                       checked={groupingMode === 'none'}
                       onChange={(e) => setGroupingMode(e.target.value)}
                     />
                   </label>
                 </div>
                 
                 <div className={`grouping-option ${groupingMode === 'league' ? 'active' : ''}`}>
                   <label onClick={() => setGroupingMode('league')}>
                     <div className="grouping-icon">🏆</div>
                     <span>Group By Price</span>
                     <input
                       type="radio"
                       name="grouping"
                       value="league"
                       checked={groupingMode === 'league'}
                       onChange={(e) => setGroupingMode(e.target.value)}
                     />
                   </label>
                 </div>
                 
                 <div className={`grouping-option ${groupingMode === 'position' ? 'active' : ''}`}>
                   <label onClick={() => setGroupingMode('position')}>
                     <div className="grouping-icon">👥</div>
                     <span>Group by Position</span>
                     <input
                       type="radio"
                       name="grouping"
                       value="position"
                       checked={groupingMode === 'position'}
                       onChange={(e) => setGroupingMode(e.target.value)}
                     />
                   </label>
                 </div>
                 
                 <div className={`grouping-option ${groupingMode === 'date' ? 'active' : ''}`}>
                   <label onClick={() => setGroupingMode('date')}>
                     <div className="grouping-icon">📅</div>
                     <span>Group by Date</span>
                     <input
                       type="radio"
                       name="grouping"
                       value="date"
                       checked={groupingMode === 'date'}
                       onChange={(e) => setGroupingMode(e.target.value)}
                     />
                   </label>
                 </div>
               </div>
            </div>
          )}
        </div>
        
        {/* Quick Views */}
        <div className="quick-views">
          <div className="quick-views-header">
            <span>Quick Views:</span>
          </div>
          <div className="quick-views-buttons">
            {Object.keys(quickViews).map(viewName => (
              <button
                key={viewName}
                className="quick-view-button"
                onClick={quickViews[viewName]}
                title={viewName}
              >
                {viewName}
              </button>
            ))}
            <button
              className="reset-button"
              onClick={resetToDefaults}
              title="Reset to Defaults"
            >
              Reset
            </button>
          </div>
        </div>

        <div className="action-icons">
          <button 
            className="icon-button"
            onClick={() => setShowStats(!showStats)}
            title="Transfer Statistics"
          >
            <BarChart3 size={20} />
          </button>
          
          <button 
            className="icon-button"
            onClick={() => setShowTable(!showTable)}
            title="Transfer Table"
          >
            <Table size={20} />
          </button>
          
          <button 
            className="icon-button"
            onClick={() => setShowHelp(!showHelp)}
            title="Help & Tips"
          >
            <HelpCircle size={20} />
          </button>
        </div>

        {/* Help Popup */}
        {showHelp && (
          <div className="popup help-popup">
            <div className="popup-header">
              <h3>Help & Tips</h3>
              <button 
                className="close-button"
                onClick={() => setShowHelp(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="help-content">
              <h4>How to use Money In Motion:</h4>
              <ul>
                <li><strong>Map Interaction:</strong> Click on transfer lines to see detailed information</li>
                <li><strong>Club Markers:</strong> Each club is represented by a colored marker based on their league</li>
                <li><strong>Transfer Lines:</strong> Lines show money flow between clubs, with thickness and color indicating transfer value</li>
                <li><strong>Search:</strong> Use the search section to find specific players or clubs</li>
                <li><strong>Filters:</strong> Use filters to focus on specific leagues, positions, transfer types, or date ranges</li>
                <li><strong>Grouping:</strong> Enable various grouping options to analyze transfers from different perspectives</li>
                <li><strong>Statistics:</strong> Click the chart icon to view detailed transfer statistics</li>
              </ul>
              
              <h4>Filter Options:</h4>
              <ul>
                <li><strong>League Filters:</strong> Filter by selling and buying leagues</li>
                <li><strong>Position Filter:</strong> Filter by player positions (Goalkeeper, Defender, Midfielder, Forward)</li>
                <li><strong>Transfer Type:</strong> Filter by free transfers, loans, or paid transfers</li>
                <li><strong>Date Range:</strong> Filter transfers within specific date periods</li>
                <li><strong>Amount Range:</strong> Use the logarithmic slider to filter by transfer amounts</li>
              </ul>
              
                             <h4>Grouping Options:</h4>
               <ul>
                 <li><strong>No Grouping:</strong> Show individual club-to-club transfers</li>
                 <li><strong>Group By Price:</strong> Show money flow grouped by price ranges (€100M+, €50M+, €20M+, Smaller)</li>
                 <li><strong>Group by Position:</strong> Show league-to-league transfers grouped by player positions (Goalkeeper, Defender, Midfielder, Forward)</li>
                 <li><strong>Group by Date Range:</strong> Show league-to-league transfers grouped by monthly periods</li>
               </ul>
               <p><em>Note: Only one grouping option can be active at a time.</em></p>
              
              <h4>Legend:</h4>
              <div className="legend">
                <div className="legend-item">
                  <div className="legend-line red"></div>
                  <span>€100M+ transfers</span>
                </div>
                <div className="legend-item">
                  <div className="legend-line orange"></div>
                  <span>€50M+ transfers</span>
                </div>
                <div className="legend-item">
                  <div className="legend-line yellow"></div>
                  <span>€20M+ transfers</span>
                </div>
                <div className="legend-item">
                  <div className="legend-line green"></div>
                  <span>Smaller transfers</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Left Side Panels */}
      {showStats && (
        <div className="left-panel stats-panel">
          <div className="panel-header">
            <h3>Transfer Statistics</h3>
            <button 
              className="close-button"
              onClick={() => setShowStats(false)}
            >
              <X size={16} />
            </button>
          </div>
          <StatsPanel 
            selectedSellerLeague={selectedSellerLeague}
            selectedBuyerLeague={selectedBuyerLeague}
            minAmount={minAmount}
            maxAmount={maxAmount}
            groupingMode={groupingMode}
            selectedPosition={selectedPosition}
            selectedTransferType={selectedTransferType}
            startDate={startDate}
            endDate={endDate}
            playerSearch={playerSearch}
            clubSearch={clubSearch}
            excludeOtherLeagues={excludeOtherLeagues}
          />
        </div>
      )}

      {showTable && (
        <div className="left-panel table-panel">
          <div className="panel-header">
            <h3>Transfer Table</h3>
            <button 
              className="close-button"
              onClick={() => setShowTable(false)}
            >
              <X size={16} />
            </button>
          </div>
          <TransferTable 
            selectedSellerLeague={selectedSellerLeague}
            selectedBuyerLeague={selectedBuyerLeague}
            minAmount={minAmount}
            maxAmount={maxAmount}
            selectedPosition={selectedPosition}
            selectedTransferType={selectedTransferType}
            startDate={startDate}
            endDate={endDate}
            playerSearch={playerSearch}
            clubSearch={clubSearch}
            excludeOtherLeagues={excludeOtherLeagues}
          />
        </div>
      )}

      {/* Map Container */}
      <div className="map-container">
        <MapContainer
          center={[50.0, 10.0]}
          zoom={5}
          style={{ height: '100vh', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
                     <TransferMap 
             key={`${selectedSellerLeague}-${selectedBuyerLeague}-${minAmount}-${maxAmount}-${selectedPosition}-${selectedTransferType}-${startDate}-${endDate}-${playerSearch}-${clubSearch}-${excludeOtherLeagues}`}
             selectedSellerLeague={selectedSellerLeague}
             selectedBuyerLeague={selectedBuyerLeague}
             minAmount={minAmount}
             maxAmount={maxAmount}
             groupingMode={groupingMode}
             selectedPosition={selectedPosition}
             selectedTransferType={selectedTransferType}
             startDate={startDate}
             endDate={endDate}
             playerSearch={playerSearch}
             clubSearch={clubSearch}
             excludeOtherLeagues={excludeOtherLeagues}
             minPercentile={minPercentile}
             maxPercentile={maxPercentile}
           />
        </MapContainer>
      </div>
    </div>
  );
}

export default App;
